import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { flushSync } from 'react-dom';

import { CustomerTransactionDetailDto, TransactionType, WalletType } from 'common/api/autogenerated/client-area';

import { Button } from 'common/components';

import { groupBy } from 'common/helpers';
import { useI18n } from 'common/hooks';
import { WalletPresenterProps } from 'common/types/wallets-new';

import { getTransactionsDetailsForCSV } from 'views/wallet-new/redux/wallet.api';

import { formatDate } from 'utils/date';

const CsvButtonComponent: React.FC<
  React.PropsWithChildren<{
    selectedWalletCurrency?: string;
    type?: WalletType;
    periodFilter?: WalletPresenterProps['transactionsPeriodFilter'];
    onProcessing?: (isProcessing: boolean) => void;
    onComplete?: () => void;
  }>
> = ({ type, periodFilter, selectedWalletCurrency, onProcessing, onComplete }) => {
  const { translate } = useI18n();

  const dateFormatted = formatDate(new Date());
  const [csvData, setCsvData] = useState<CustomerTransactionDetailDto[] | undefined>(undefined);
  const [isProcessing, updateProcessingState] = useState(false);

  const fetchUntilEnoughData = useCallback(async () => {
    const collectedData: CustomerTransactionDetailDto[] = [];
    let page = 0;
    let max = Infinity;

    // Loop until we have collected enough data
    while (collectedData.length < max) {
      page += 1;

      // Fetch paginated transaction details

      const { data, total = 0 } = await getTransactionsDetailsForCSV(
        type,
        selectedWalletCurrency,
        periodFilter?.startDate,
        periodFilter?.endDate,
        page,
      );

      max = total;

      // Collect the new data from the current page
      if (data) collectedData.push(...data);

      // Stop if we have collected enough data
      if (collectedData.length >= total) {
        const grouped = groupBy(collectedData, (item) => item.type ?? '');

        flushSync(() => {
          // Prepare CSV data based on grouped transaction types
          setCsvData(
            [
              TransactionType.DEPOSIT,
              TransactionType.WITHDRAWAL,
              TransactionType.RECEIVE,
              TransactionType.SEND,
              TransactionType.BONUS,
              TransactionType.FEE,
            ]
              .filter((key) => key in grouped)
              .map((key) => grouped[key])
              .flat(),
          );
        });

        break; // Exit the loop since we have enough data
      }
    }

    return collectedData;
  }, [periodFilter?.endDate, periodFilter?.startDate, selectedWalletCurrency, type]);

  const getTransactionsDetails = useCallback(
    async (page = 1) => {
      let transactions: CustomerTransactionDetailDto[] = [];

      await getTransactionsDetailsForCSV(
        type,
        selectedWalletCurrency,
        periodFilter?.startDate,
        periodFilter?.endDate,
        page,
      ).then(({ data, total = 0 }) => {
        if (data) {
          const update = [...transactions, ...data];
          transactions = update;

          if (update.length < total) getTransactionsDetails(page + 1);
        }
      });
    },
    [type, selectedWalletCurrency, periodFilter?.startDate, periodFilter?.endDate],
  );

  useEffect(() => setCsvData(undefined), [type, selectedWalletCurrency, periodFilter]);

  const headers: Array<{
    label: string;
    key: keyof CustomerTransactionDetailDto;
  }> = useMemo(
    () => [
      { key: 'id', label: 'Transaction ID' },
      { key: 'symbol', label: 'Currency' },
      { key: 'state', label: 'Transaction status' },
      { key: 'type', label: 'Transaction type' },
      { key: 'paymentMethod', label: 'Transaction method' },
      { key: 'currencyType', label: 'Currency type' },
      { key: 'createdOn', label: 'Date' },
      { key: 'totalAmount', label: 'Total amount' },
      { key: 'executedAmount', label: 'Received amount' },
      { key: 'fee', label: 'Fee' },
      { key: 'customerComment', label: 'Comment' },
      { key: 'networkCode', label: 'Network code' },
      { key: 'networkName', label: 'Network name' },
      { key: 'destinationAddress', label: 'Address' },
      { key: 'networkTransactionId', label: 'Transaction hash' },
      { key: 'destinationTag', label: 'Tag' },
      { key: 'cardAssociationName', label: 'Card' },
      { key: 'lastCreditCardDigits', label: 'Card number' },
      { key: 'bankCountryCode', label: 'Bank country code' },
      { key: 'bankCountryName', label: 'Bank country name' },
      { key: 'bankAccount', label: 'Bank account' },
      { key: 'bankName', label: 'Bank name' },
      { key: 'paymentIntentId', label: 'Trace ID' },
      { key: 'cryptoPayTransactionId', label: 'Trace ID' },
    ],
    [],
  );

  const onClick = useCallback(
    // TODO: fix type
    (event: unknown) => {
      const { currentTarget } = event as SyntheticEvent<HTMLAnchorElement>; // TODO: fix type
      if (csvData === undefined) {
        onProcessing?.(true);
        updateProcessingState(true);
        // if data don't exist - we get it from API and make a repeated click
        fetchUntilEnoughData()
          .then(() => currentTarget?.click?.())
          .then(() => {
            onComplete?.();
            updateProcessingState(false);
          });
        return false;
      }
      return true;
    },
    [csvData, fetchUntilEnoughData, onComplete, onProcessing],
  );

  return (
    <CSVLink
      onClick={onClick}
      data-test='wallet-transactions-get-csv'
      filename={`xbo-transactions-history-${dateFormatted}`}
      data={csvData ?? []}
      headers={headers}
    >
      <Button large stretch loading={isProcessing}>
        {translate.toText('wallet:transactions:export:confirm')}
      </Button>
    </CSVLink>
  );
};

export default React.memo(CsvButtonComponent);
